import PageShell from '@/pages/shared/PageShell';

export default function AiDailyReport() {
  return (
    <PageShell
      title="AI日報｜自由な文章からプロジェクト進捗を自動更新｜”おもいむすび”"
      description="メンバーが自由な文章で日報を入力すると、AIが内容を解析。完了した作業、遅れている作業、次のタスク、課題を自動整理し、該当タスクの進捗率を更新します。"
      keywords="プロジェクト管理 日報,AI 日報 自動化,日報 AI,AI 進捗管理,プロジェクト進捗 自動更新"
      canonical="https://omoimusubi.asia/features/ai-daily-report/"
      h1="自由な日報から、AIがプロジェクトの進捗を自動更新"
      subtitle="メンバーが自由な文章で日報や進捗報告を入力すると、AIが内容を解析し、進捗表を自動で更新します。"
      relatedLinks={[
        { label: 'AIガントチャート', path: '/features/ai-gantt/' },
        { label: 'プロジェクト進捗管理ガイド', path: '/guides/project-progress-management/' },
        { label: 'プロジェクト日報ガイド', path: '/guides/project-daily-report/' },
      ]}
    >
      <div className="not-prose mb-8 rounded-xl overflow-hidden border border-background-200/70">
        <img
          src="https://readdy.ai/api/search-image?query=A%20Japanese%20female%20office%20worker%20in%20her%2030s%20typing%20on%20a%20laptop%20at%20a%20bright%20modern%20desk%20in%20a%20sunlit%20Japanese%20office%20with%20large%20windows%20natural%20daylight%20the%20laptop%20screen%20shows%20a%20daily%20report%20writing%20interface%20in%20Japanese%20with%20a%20cheerful%20expression%20of%20accomplishment%20warm%20atmosphere%20indoor%20plants%20on%20the%20desk%20clean%20minimalist%20workspace%20refreshing%20professional%20environment&width=1200&height=700&seq=feature-ai-daily-report-01&orientation=landscape&nocache=true"
          alt="AI日報の製品画面 - 自由な文章で報告するだけでAIが進捗を自動解析"
          className="w-full h-auto"
        />
      </div>

      <div className="not-prose my-8 bg-background-100 rounded-lg border border-background-200/70 overflow-hidden">
        <div className="grid grid-cols-1 lg:grid-cols-2">
          <div className="p-6 lg:p-8">
            <h2 className="text-base font-bold text-foreground-900 mb-3">日報入力画面</h2>
            <div className="bg-background-50 rounded-md p-4 border border-background-200/70">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-7 h-7 bg-primary-100 rounded-full flex items-center justify-center text-xs font-bold text-primary-700">T</div>
                <div>
                  <p className="text-xs font-medium text-foreground-800">田中 太郎</p>
                  <p className="text-[10px] text-foreground-500">2026年7月18日（金）</p>
                </div>
              </div>
              <div className="bg-white rounded p-3 border border-background-200 text-xs text-foreground-700 leading-relaxed">
                <p>【本日の作業】</p>
                <p>・ログイン画面のデザインカンプ作成が完了しました。</p>
                <p>・API接続部分の仕様について、認証方式の確認が必要です。</p>
                <p className="mt-2">【明日の予定】</p>
                <p>・エラー画面（404/500）のデザイン作成に着手します。</p>
                <p className="mt-2">【課題】</p>
                <p>・認証方式が確定しないとAPI接続の実装に入れません。</p>
              </div>
              <div className="flex justify-end mt-3">
                <span className="px-4 py-1.5 bg-primary-500 text-background-50 rounded-md text-xs font-medium cursor-pointer">報告する</span>
              </div>
            </div>
          </div>
          <div className="p-6 lg:p-8 bg-primary-50 border-l border-background-200/70">
            <h2 className="text-base font-bold text-foreground-900 mb-3">AI解析結果</h2>
            <p className="text-xs text-primary-700 mb-3">AIが日報内容を自動解析し、以下のように整理しました。</p>
            <div className="space-y-3 text-xs">
              <div className="bg-background-50 rounded-md p-3 border border-background-200/70">
                <p className="font-medium text-foreground-900 mb-2">✅ 完了した作業</p>
                <p className="text-foreground-700">ログイン画面デザインカンプ → <span className="text-green-600 font-medium">進捗率 100%に更新</span></p>
              </div>
              <div className="bg-background-50 rounded-md p-3 border border-background-200/70">
                <p className="font-medium text-foreground-900 mb-2">⏳ 進行中・確認待ち</p>
                <p className="text-foreground-700">API接続 → <span className="text-amber-600 font-medium">認証方式確認待ち</span></p>
              </div>
              <div className="bg-background-50 rounded-md p-3 border border-background-200/70">
                <p className="font-medium text-foreground-900 mb-2">📋 次のタスク</p>
                <p className="text-foreground-700">エラー画面（404/500）デザイン作成</p>
              </div>
              <div className="bg-background-50 rounded-md p-3 border border-background-200/70">
                <p className="font-medium text-foreground-900 mb-2">⚠️ 検出された課題</p>
                <p className="text-foreground-700">認証方式の確定が必要（API接続のブロッカー）</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <section>
        <h2>AIが日報から整理する内容</h2>
        <p>メンバーが自由な文章で入力した日報から、AIが以下の情報を自動で抽出・整理します。</p>
        <div className="not-prose grid grid-cols-1 sm:grid-cols-2 gap-3 my-4">
          {[
            { icon: 'ri-checkbox-circle-line', title: '完了した作業', desc: '日報の記述から、どのタスクが完了したかを判定し、進捗率を自動更新します。' },
            { icon: 'ri-loader-4-line', title: '進行中の作業', desc: '現在取り組んでいる作業の進捗状況を把握します。' },
            { icon: 'ri-timer-line', title: '遅れている作業', desc: '予定に対して遅延が発生している作業を検出し、アラートを表示します。' },
            { icon: 'ri-error-warning-line', title: '発生した問題', desc: '課題や障害として報告された内容を抽出し、一覧化します。' },
            { icon: 'ri-arrow-right-line', title: '次に行う作業', desc: '翌日以降の予定を自動で整理します。' },
            { icon: 'ri-question-answer-line', title: '確認事項', desc: '上司や管理者への判断・確認が必要な事項を一覧化します。' },
            { icon: 'ri-radar-line', title: 'プロジェクト全体への影響', desc: '報告内容が他のタスクやプロジェクト全体に与える影響を分析します。' },
          ].map((item) => (
            <div key={item.title} className="flex items-start gap-3 bg-background-100 rounded-lg p-4 border border-background-200/70">
              <div className="w-9 h-9 bg-accent-100 rounded-lg flex items-center justify-center shrink-0">
                <i className={`${item.icon} text-accent-600 text-sm`}></i>
              </div>
              <div>
                <h3 className="text-sm font-bold text-foreground-900">{item.title}</h3>
                <p className="text-xs text-foreground-600">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section>
        <h2>日報の入力方法</h2>
        <p>決まったフォーマットは必要ありません。普段の業務報告と同じように、自由な文章でその日の作業内容や気づいたことを記入してください。AIが文章を解析し、適切に情報を整理します。</p>
        <p className="text-sm text-foreground-500 mt-2">「今日やったこと」「明日の予定」「課題」を含めると、AIの解析精度がより高まります。</p>
      </section>

      <section>
        <h2>よくある質問</h2>
        <h3>日報の形式に決まりはありますか</h3>
        <p>いいえ。自由な文章で入力できます。箇条書きでも、段落形式の文章でも、AIが内容を解析します。ただし「やったこと」「予定」「課題」を含めると解析精度が高まります。</p>
        <h3>AIが誤った解析をした場合はどうなりますか</h3>
        <p>AIの解析結果は、ガントチャートへの反映前にユーザーが確認できます。誤りがあれば手動で修正してから反映することが可能です。</p>
      </section>
    </PageShell>
  );
}